import type { BulkImportReport } from "../types";
import { CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface ImportResultsBannerProps {
  report: BulkImportReport;
  onDismiss: () => void;
}

export function ImportResultsBanner({
  report,
  onDismiss,
}: ImportResultsBannerProps) {
  const { t } = useLocale();
  const isSuccess = report.errorCount === 0;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-4 ${
        isSuccess
          ? "border-success/30 bg-success/5"
          : "border-warning/30 bg-warning/5"
      }`}
    >
      {isSuccess ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
      ) : (
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
      )}

      <div className="flex-1 text-sm">
        <p className="font-medium text-foreground">
          {isSuccess ? t("roster.importSuccessful") : t("roster.importWithIssues")}
        </p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" />
            {t("roster.importedCount", { count: String(report.successCount) })}
          </span>
          {report.skippedCount > 0 && (
            <span className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
              {t("roster.skippedCount", { count: String(report.skippedCount) })}
            </span>
          )}
          {report.errorCount > 0 && (
            <span className="flex items-center gap-1">
              <XCircle className="h-3.5 w-3.5 text-destructive" />
              {t("roster.errorCount", { count: String(report.errorCount) })}
            </span>
          )}
        </div>
        {report.errors.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              {t("roster.viewErrors", { count: String(report.errors.length) })}
            </summary>
            <div className="mt-1 max-h-32 overflow-y-auto rounded border border-border bg-background p-2">
              {report.errors.slice(0, 20).map((err, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  <span className="font-mono text-destructive">
                    Row {err.row}:
                  </span>{" "}
                  {err.error}
                </p>
              ))}
              {report.errors.length > 20 && (
                <p className="text-xs text-muted-foreground">
                  {t("roster.andMore", { count: String(report.errors.length - 20) })}
                </p>
              )}
            </div>
          </details>
        )}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
